// src/tenantAdmin/components/AddCompanyAdminDialog.jsx
//
// «Aggiungi amministratore» — the address of the person to make an administrator of the company (v202, the
// company-admins section of the Utenti tab). The role is always `admin`; there is nothing else to ask.
//
// API-agnostic like CorrectEmailDialog: the caller hands `onSubmit(email)`, which resolves on success (the
// caller then owns the outcome) or throws. A refusal is worded HERE and the dialog stays open on the address.
//
// ⛔⛔ 404 USER_NOT_FOUND is worded BEFORE `userActionErrorCopy`: that helper reads it as "no longer available",
// the opposite of the truth on this route — the address has no account yet and needs one first.
// ⛔ Never `err.message`.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../../admin/components/AdminModal.jsx';
import { userActionErrorCopy } from '../userActionError.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const fieldLabel = {
  display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: COLORS.textSecondary, fontFamily: FONT_SANS,
  marginBottom: '8px',
};
const field = {
  width: '100%', height: '46px', boxSizing: 'border-box',
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, color: COLORS.ink, fontSize: '14px',
  fontFamily: FONT_SANS, outline: 'none', padding: '0 14px',
};

export function addAdminRefusal(t, err) {
  const code = err?.code;
  if (code === 'USER_NOT_FOUND') return t('manage.members.notFound');
  if (code === 'CUSTOMER_USER_EXISTS') return t('manage.members.exists');
  // People, not workspaces: the company-wide person cap.
  if (code === 'USER_SEAT_LIMIT_REACHED') return t('manage.members.seatLimit');
  // An account that is switched off cannot be attached; a platform administrator is never a company member.
  if (code === 'AUTH_USER_DEACTIVATED') return t('tenantUsers.accountInactive');
  // Invited to a workspace, password never set — a different remedy (BE 7c5dc05).
  if (code === 'AUTH_USER_PENDING_ONBOARDING') return t('tenantUsers.pendingOnboarding');
  if (code === 'CANNOT_ATTACH_SUPERADMIN') return t('tenantUsers.cannotInviteSuperadmin');
  // 429 (shared per-address budget) and everything else, including the generic sentence with the code.
  return userActionErrorCopy(t, err);
}

export default function AddCompanyAdminDialog({ open, onSubmit, onClose }) {
  const { t } = useTranslation('admin');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setError('');
    setLoading(false);
  }, [open]);

  const next = email.trim();

  async function handleSubmit(e) {
    e.preventDefault();
    if (next === '' || loading) return;
    setError('');
    setLoading(true);
    try {
      await onSubmit(next);
      onClose();
    } catch (err) {
      setError(addAdminRefusal(t, err));
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    if (loading) return; // a write is in flight; its outcome still has to land
    onClose();
  }

  const blocked = loading || next === '';
  return (
    <AdminModal open={open} onClose={handleClose} title={t('manage.members.add')} width={460} role="dialog">
      <form onSubmit={handleSubmit}>
        <label htmlFor="cad-email" style={fieldLabel}>{t('shared.email')}</label>
        <input
          id="cad-email"
          type="email"
          value={email}
          autoComplete="off"
          placeholder={t('tenantUsers.emailPlaceholder')}
          onChange={e => setEmail(e.target.value)}
          style={field}
        />

        {error && (
          <div role="alert" style={{
            marginTop: '16px', padding: '10px 14px', borderRadius: RADII.controlInner,
            background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
            color: SEMANTIC.errore.fg, fontSize: '13px', fontFamily: FONT_SANS,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            style={{
              height: '40px', padding: '0 18px', borderRadius: RADII.controlInner,
              border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
              color: COLORS.ink, fontSize: '13px', fontFamily: FONT_SANS,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1,
            }}
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={blocked}
            style={{
              height: '40px', padding: '0 18px', borderRadius: RADII.controlInner, border: 'none',
              background: blocked ? COLORS.borderStrong : COLORS.accent,
              color: COLORS.surface, fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
              cursor: blocked ? 'not-allowed' : 'pointer',
            }}
          >
            {t('manage.members.addSubmit')}
          </button>
        </div>
      </form>
    </AdminModal>
  );
}
